// Snap computation helper — used by both box-drag and draft-box placement in
// FlowchartBuilder.tsx. Pure geometry, no React state — lives here (not inline in the
// component) so it can be unit-tested on its own (see snapping.test.ts).
import type { SnapLine } from '../types/index';

export interface BoxShape { x: number; y: number; width: number; height: number; }

export function computeSnap(
  moving: BoxShape,
  otherBoxes: BoxShape[],
  snapThreshold: number
): { snapX: number | null; snapY: number | null; lines: SnapLine[] } {
  const lines: SnapLine[] = [];
  let snapX: number | null = null;
  let snapY: number | null = null;

  if (otherBoxes.length === 0) return { snapX, snapY, lines };

  const dragLeft = moving.x;
  const dragRight = moving.x + moving.width;
  const dragTop = moving.y;
  const dragBottom = moving.y + moving.height;
  const dragCenterX = moving.x + moving.width / 2;
  const dragCenterY = moving.y + moving.height / 2;

  // Edge / center alignment snapping
  otherBoxes.forEach(other => {
    const otherLeft = other.x;
    const otherRight = other.x + other.width;
    const otherTop = other.y;
    const otherBottom = other.y + other.height;
    const otherCenterX = other.x + other.width / 2;
    const otherCenterY = other.y + other.height / 2;

    if (Math.abs(dragLeft - otherLeft) < snapThreshold) {
      snapX = otherLeft;
      lines.push({ type: 'vertical', x: otherLeft, y1: Math.min(dragTop, otherTop), y2: Math.max(dragBottom, otherBottom) });
    }
    if (Math.abs(dragRight - otherRight) < snapThreshold) {
      snapX = otherRight - moving.width;
      lines.push({ type: 'vertical', x: otherRight, y1: Math.min(dragTop, otherTop), y2: Math.max(dragBottom, otherBottom) });
    }
    if (Math.abs(dragCenterX - otherCenterX) < snapThreshold) {
      snapX = otherCenterX - moving.width / 2;
      lines.push({ type: 'vertical', x: otherCenterX, y1: Math.min(dragTop, otherTop), y2: Math.max(dragBottom, otherBottom) });
    }
    if (Math.abs(dragTop - otherTop) < snapThreshold) {
      snapY = otherTop;
      lines.push({ type: 'horizontal', y: otherTop, x1: Math.min(dragLeft, otherLeft), x2: Math.max(dragRight, otherRight) });
    }
    if (Math.abs(dragBottom - otherBottom) < snapThreshold) {
      snapY = otherBottom - moving.height;
      lines.push({ type: 'horizontal', y: otherBottom, x1: Math.min(dragLeft, otherLeft), x2: Math.max(dragRight, otherRight) });
    }
    if (Math.abs(dragCenterY - otherCenterY) < snapThreshold) {
      snapY = otherCenterY - moving.height / 2;
      lines.push({ type: 'horizontal', y: otherCenterY, x1: Math.min(dragLeft, otherLeft), x2: Math.max(dragRight, otherRight) });
    }
  });

  // Equidistance snapping
  const EQUIDIST_SNAP = 15;
  const EQUIDIST_ROW_TOL = 30;
  const EQUIDIST_NEAR_ROW = 80;

  // Pre-filter before the O(n²) pairwise loops below — this was a plain
  // otherBoxes×otherBoxes double loop with no pre-filtering, recomputed on every single
  // mousemove frame while dragging, which got noticeably slow on flowcharts with 100+
  // boxes. The horizontal check requires |aCY-bCY| < EQUIDIST_ROW_TOL AND their average
  // within EQUIDIST_NEAR_ROW of dragCenterY; together those bound EACH box's own centerY
  // to within EQUIDIST_NEAR_ROW + EQUIDIST_ROW_TOL/2 of dragCenterY (if two numbers are
  // within ROW_TOL of each other, each is within ROW_TOL/2 of their average). This is an
  // exact bound, not an approximation — any pair that could pass the real check survives
  // this filter — so the set of detected snaps is unchanged; only pairs that could never
  // match are skipped. Same reasoning for the vertical check via centerX/dragCenterX.
  const NEAR_BOUND = EQUIDIST_NEAR_ROW + EQUIDIST_ROW_TOL / 2;
  const rowCandidates = otherBoxes.filter(b => Math.abs((b.y + b.height / 2) - dragCenterY) < NEAR_BOUND);
  const colCandidates = otherBoxes.filter(b => Math.abs((b.x + b.width / 2) - dragCenterX) < NEAR_BOUND);

  for (let i = 0; i < rowCandidates.length; i++) {
    for (let j = i + 1; j < rowCandidates.length; j++) {
      const a = rowCandidates[i];
      const b = rowCandidates[j];
      const aCX = a.x + a.width / 2;
      const aCY = a.y + a.height / 2;
      const bCX = b.x + b.width / 2;
      const bCY = b.y + b.height / 2;

      const rowY = (aCY + bCY) / 2;
      if (
        Math.abs(aCY - bCY) < EQUIDIST_ROW_TOL &&
        Math.abs(dragCenterY - rowY) < EQUIDIST_NEAR_ROW &&
        Math.abs(aCX - bCX) > EQUIDIST_SNAP * 2
      ) {
        const spacingX = bCX - aCX;
        const candidates = [
          { snapPos: (aCX + bCX) / 2, anchor1: aCX, anchor2: bCX },
          { snapPos: bCX + spacingX,   anchor1: aCX, anchor2: bCX + spacingX },
          { snapPos: aCX - spacingX,   anchor1: aCX - spacingX, anchor2: bCX },
        ];
        for (const { snapPos, anchor1, anchor2 } of candidates) {
          if (Math.abs(dragCenterX - snapPos) < EQUIDIST_SNAP) {
            if (snapX === null) snapX = snapPos - moving.width / 2;
            const sorted = [anchor1, anchor2, snapPos].sort((p, q) => p - q);
            lines.push({ type: 'equidistance-h', y: rowY, x1: sorted[0], x2: sorted[2], xMid: sorted[1] });
            break;
          }
        }
      }
    }
  }

  for (let i = 0; i < colCandidates.length; i++) {
    for (let j = i + 1; j < colCandidates.length; j++) {
      const a = colCandidates[i];
      const b = colCandidates[j];
      const aCX = a.x + a.width / 2;
      const aCY = a.y + a.height / 2;
      const bCX = b.x + b.width / 2;
      const bCY = b.y + b.height / 2;

      const colX = (aCX + bCX) / 2;
      if (
        Math.abs(aCX - bCX) < EQUIDIST_ROW_TOL &&
        Math.abs(dragCenterX - colX) < EQUIDIST_NEAR_ROW &&
        Math.abs(aCY - bCY) > EQUIDIST_SNAP * 2
      ) {
        const spacingY = bCY - aCY;
        const candidatesV = [
          { snapPos: (aCY + bCY) / 2, anchor1: aCY, anchor2: bCY },
          { snapPos: bCY + spacingY,   anchor1: aCY, anchor2: bCY + spacingY },
          { snapPos: aCY - spacingY,   anchor1: aCY - spacingY, anchor2: bCY },
        ];
        for (const { snapPos, anchor1, anchor2 } of candidatesV) {
          if (Math.abs(dragCenterY - snapPos) < EQUIDIST_SNAP) {
            if (snapY === null) snapY = snapPos - moving.height / 2;
            const sorted = [anchor1, anchor2, snapPos].sort((p, q) => p - q);
            lines.push({ type: 'equidistance-v', x: colX, y1: sorted[0], y2: sorted[2], yMid: sorted[1] });
            break;
          }
        }
      }
    }
  }

  return { snapX, snapY, lines };
}
